// // src/scripts/shared/weekLimits.js

// /**
//  * Centralised "weeks forward" planning window used across the app.
//  *
//  * Meaning:
//  * - 0  = only the current week
//  * - 4  = current week + 4 weeks (5 weeks total including current)
//  * - 10 = current week + 10 weeks (11 weeks total including current)
//  *
//  * Adjust this during busy periods when you need to plan further ahead.
//  */
// export const MAX_WEEKS_FORWARD = 12;

// src/scripts/sharedHelpers/weeklyLimits.js

/**
 * Centralised "weeks forward" planning window used across the app.
 *
 * Normal operation:
 * - 6 = current week + 6 weeks
 *
 * Seasonal planning windows:
 *
 * NEW YEAR:
 * - From 12 weeks before New Year's Eve
 * - Until and including 31 December
 * - MAX_WEEKS_FORWARD = 12
 *
 * EASTER:
 * - From 9 weeks before Easter Sunday
 * - Until and including Easter Sunday
 * - MAX_WEEKS_FORWARD = 9
 *
 * Once either seasonal period has passed,
 * MAX_WEEKS_FORWARD automatically returns to 6.
 */

const DEFAULT_WEEKS_FORWARD = 6;
const NEW_YEAR_WEEKS_FORWARD = 13;
const EASTER_WEEKS_FORWARD = 9;

/**
 * Remove the time portion of a Date.
 */
function startOfDay(date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}

/**
 * Add or subtract days from a Date.
 */
function addDays(date, days) {
  const d = new Date(date);
  d.setDate(d.getDate() + Number(days || 0));
  return startOfDay(d);
}

/**
 * Calculate Easter Sunday for a given year.
 *
 * Gregorian Easter calculation.
 */
function getEasterSunday(year) {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);

  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;

  return startOfDay(new Date(year, month - 1, day));
}

/**
 * Check whether today falls inside the New Year
 * extended planning window.
 *
 * Window:
 * 12 weeks before 31 December
 * through to 31 December inclusive.
 */
function isInNewYearPlanningWindow(today) {
  const currentDate = startOfDay(today);
  const year = currentDate.getFullYear();

  const newYearsEve = startOfDay(new Date(year, 11, 31));

  const windowStart = addDays(
    newYearsEve,
    -(NEW_YEAR_WEEKS_FORWARD * 7)
  );

  return (
    currentDate >= windowStart &&
    currentDate <= newYearsEve
  );
}

/**
 * Check whether today falls inside the Easter
 * extended planning window.
 *
 * Window:
 * 9 weeks before Easter Sunday
 * through to Easter Sunday inclusive.
 */
function isInEasterPlanningWindow(today) {
  const currentDate = startOfDay(today);
  const year = currentDate.getFullYear();

  const easterSunday = getEasterSunday(year);

  const windowStart = addDays(
    easterSunday,
    -(EASTER_WEEKS_FORWARD * 7)
  );

  return (
    currentDate >= windowStart &&
    currentDate <= easterSunday
  );
}

/**
 * Calculate the number of weeks forward that
 * CoreDeck should currently allow.
 *
 * Priority:
 * 1. New Year = 12 weeks
 * 2. Easter = 9 weeks
 * 3. Normal = 6 weeks
 */
function calculateMaxWeeksForward(today = new Date()) {
  const currentDate = startOfDay(today);

  if (isInNewYearPlanningWindow(currentDate)) {
    return NEW_YEAR_WEEKS_FORWARD;
  }

  if (isInEasterPlanningWindow(currentDate)) {
    return EASTER_WEEKS_FORWARD;
  }

  return DEFAULT_WEEKS_FORWARD;
}

/**
 * This is the value imported by the rest of CoreDeck.
 *
 * It is calculated when the app loads:
 *
 * Normal:
 * MAX_WEEKS_FORWARD = 6
 *
 * Easter planning window:
 * MAX_WEEKS_FORWARD = 9
 *
 * New Year planning window:
 * MAX_WEEKS_FORWARD = 12
 */
export const MAX_WEEKS_FORWARD = calculateMaxWeeksForward();